<template>
  <template v-for="(parts, index) in pageParts" :key="parts.document_id">
    <template v-if="!childPartsIds?.includes(parts.document_id)">
      <PagePartsText
        v-if="parts.layout_type === 'text'"
        :id="`page_parts_${parts.document_id}`"
        :title="parts.detail.title"
        :sp-title="parts.detail.sp_title"
        :font-size="fontSizeClasses(parts.detail.font_size ?? '', parts.detail.sp_font_size ?? '')"
        :font-weight="fontWeightClasses(parts.detail.font_weight)"
        :text-align="alignClasses(parts.detail.text_align, parts.detail.sp_text_align)"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsEditorText
        v-else-if="parts.layout_type === 'editor_text'"
        :id="`page_parts_${parts.document_id}`"
        :text="parts.detail.title ?? ''"
        :sp-text="parts.detail.sp_title"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsImageAndText
        v-else-if="parts.layout_type === 'image_and_text'"
        :id="`page_parts_${parts.document_id}`"
        :title="parts.detail.title"
        :sp-title="parts.detail.sp_title"
        :description="parts.detail.description"
        :sp-description="parts.detail.sp_description"
        :img-url="parts.detail.img_url"
        :sp-img-url="parts.detail.sp_img_url"
        :text-orientation="parts.detail.orientation"
        :img-url-caption="parts.detail.img_url_caption"
        :sp-img-url-caption="parts.detail.sp_img_url_caption"
        :font-size="fontSizeClasses(parts.detail.font_size ?? '', parts.detail.sp_font_size ?? '')"
        :desc-font-size="fontSizeClasses(parts.detail.desc_font_size ?? '', parts.detail.sp_desc_font_size ?? '')"
        :font-weight="fontWeightClasses(parts.detail.font_weight)"
        :display-type="parts.display_type"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsImage
        v-else-if="parts.layout_type === 'image'"
        :id="`page_parts_${parts.document_id}`"
        :img-url="parts.detail.img_url"
        :img-url-caption="parts.detail.img_url_caption"
        :sp-img-url="parts.detail.sp_img_url"
        :sp-img-url-caption="parts.detail.sp_img_url_caption"
        :url="parts.detail.url"
        :display-type="parts.display_type"
        :page-type="pageType"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsVideo
        v-else-if="parts.layout_type === 'video'"
        :id="`page_parts_${parts.document_id}`"
        :key="parts.document_id"
        :pc-url="parts.detail.video_url"
        :sp-url="parts.detail.sp_video_url"
        :pc-auto-play="parts.detail.video_auto_play"
        :sp-auto-play="parts.detail.sp_video_auto_play"
        :pc-loop-playback="parts.detail.video_loop_playback"
        :sp-loop-playback="parts.detail.sp_video_loop_playback"
        :pc-controls="parts.detail.video_controls"
        :sp-controls="parts.detail.sp_video_controls"
        :page-type="pageType"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsImageSlide
        v-else-if="parts.layout_type === 'image_slide' || parts.layout_type === 'image_scroll'"
        :id="`page_parts_${parts.document_id}`"
        :img-list="parts.list"
        :pc-img-column="parts.detail.pc_img_column"
        :sp-img-column="parts.detail.sp_img_column"
        :display-type="parts.display_type"
        :page-type="pageType"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsModalBanner
        v-else-if="parts.layout_type === 'modal_banner'"
        :id="`page_parts_${parts.document_id}`"
        :title="parts.detail.title"
        :description="parts.detail.description"
        :bg-color="parts.detail.bg_color"
        :text-color="parts.detail.text_color"
        :banner-image="parts.detail.common_img_url"
        :modal-image="parts.detail.img_url"
        :sp-modal-image="parts.detail.sp_img_url"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsBottomBlurCardList
        v-else-if="parts.layout_type === 'bottom_blur_card_list'"
        :id="`page_parts_${parts.document_id}`"
        :images="parts.list"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsLargeSlideContents
        v-else-if="parts.layout_type === 'large_slide_contents'"
        :id="`page_parts_${parts.document_id}`"
        :large-slide-contents="parts.list"
        :font-size="fontSizeClasses(parts.detail.font_size ?? '', parts.detail.sp_font_size ?? '')"
        :font-weight="fontWeightClasses(parts.detail.font_weight)"
        :sub-font-size="fontSizeClasses(parts.detail.sub_font_size ?? '', parts.detail.sp_sub_font_size ?? '')"
        :sub-font-weight="fontWeightClasses(parts.detail.sub_font_weight)"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsBlockLink
        v-else-if="parts.layout_type === 'block_link'"
        :id="`page_parts_${parts.document_id}`"
        :block-links="parts.list"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsCatchphrase
        v-else-if="parts.layout_type === 'catchphrase'"
        :id="`page_parts_${parts.document_id}`"
        :title="parts.detail.title"
        :sp-title="parts.detail.sp_title"
        :description="parts.detail.description"
        :subtitle="parts.detail.subtitle"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsThumbnailsList
        v-else-if="parts.layout_type === 'thumbnail_list'"
        :id="`page_parts_${parts.document_id}`"
        :thumbnails="parts.list"
        :link-url="parts.detail.url"
        :font-size="fontSizeClasses(parts.detail.font_size ?? '', parts.detail.sp_font_size ?? '')"
        :sub-font-size="fontSizeClasses(parts.detail.sub_font_size ?? '', parts.detail.sp_sub_font_size ?? '')"
        :font-weight="fontWeightClasses(parts.detail.font_weight)"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsHeadText
        v-else-if="parts.layout_type === 'head_text'"
        :id="`page_parts_${parts.document_id}`"
        :title="parts.detail.title"
        :sub-title="parts.detail.subtitle"
        :pc-title-font-size-px="Number(parts.detail.font_size ?? 0)"
        :pc-sub-title-font-size-px="Number(parts.detail.sub_font_size ?? 0)"
        :sp-title-font-size-px="Number(parts.detail.sp_font_size ?? 0)"
        :sp-sub-title-font-size-px="Number(parts.detail.sp_sub_font_size ?? 0)"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <!-- -------------------------------------------------------------- -->
      <!-- 新規にRecommendProductsV2を作成し順次移し替え、以後こちらは使用しない -->
      <!-- データの持ち方が変わり、書き換えが難しいのでpagePartsとしては残しています -->
      <PagePartsRecommendProducts
        v-else-if="parts.layout_type === 'recommend_product_list'"
        :id="`page_parts_${parts.document_id}`"
        :products="parts.list"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <!-- -------------------------------------------------------------- -->
      <PagePartsRecommendProductsV2
        v-else-if="parts.layout_type === 'recommend_product_list_v2'"
        :id="`page_parts_${parts.document_id}`"
        :products="parts.detail.is_ai_recommend ? parts.recommendProducts as PagePartsProductType[] : parts.products"
        :is-ai-recommend="parts.detail.is_ai_recommend"
        :ai-recommend-number="parts.detail.ai_recommend_number"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsRecommendProductListByPrice
        v-else-if="parts.layout_type === 'recommend_product_list_by_price'"
        :id="`page_parts_${parts.document_id}`"
        :products="parts.products as PagePartsProductType[]"
        :list-parts="parts.list"
        :bg-color="parts.detail.bg_color"
        :is-open-modal="parts.detail.is_img_navigation"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsRecommendProductListByScene
        v-else-if="parts.layout_type === 'recommend_product_list_by_scene'"
        :id="`page_parts_${parts.document_id}`"
        :products="parts.products as PagePartsProductType[]"
        :list-parts="parts.list"
        :product-id-and-categories="parts.product_id_and_categories"
        :bg-color="parts.detail.bg_color"
        :is-open-modal="parts.detail.is_img_navigation"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <template v-else-if="parts.layout_type === 'product_browsing_history'">
        <ClientOnly>
          <PagePartsProductBrowsingHistory
            :id="`page_parts_${parts.document_id}`"
            :title="parts.detail.title"
            :sub-title="parts.detail.subtitle"
            :pc-title-font-size-px="Number(parts.detail.font_size ?? 0)"
            :pc-sub-title-font-size-px="Number(parts.detail.sub_font_size ?? 0)"
            :sp-title-font-size-px="Number(parts.detail.sp_font_size ?? 0)"
            :sp-sub-title-font-size-px="Number(parts.detail.sp_sub_font_size ?? 0)"
            class="page-parts-section"
            :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
          />
        </ClientOnly>
      </template>
      <PagePartsSmallImageTextToDetail
        v-else-if="parts.layout_type === 'small_image_text_to_detail'"
        :id="`page_parts_${parts.document_id}`"
        :title="parts.detail.title"
        :sp-title="parts.detail.sp_title"
        :description="parts.detail.description"
        :sp-description="parts.detail.sp_description"
        :font-size="fontSizeClasses(parts.detail.font_size ?? '', parts.detail.sp_font_size ?? '')"
        :desc-font-size="fontSizeClasses(parts.detail.desc_font_size ?? '', parts.detail.sp_desc_font_size ?? '')"
        :img-url="parts.detail.img_url"
        :sp-img-url="parts.detail.sp_img_url"
        :img-url-caption="parts.detail.img_url_caption"
        :sp-img-url-caption="parts.detail.sp_img_url_caption"
        :link-url="parts.detail.url"
        :link-text="parts.detail.subtitle"
        :bg-color="parts.detail.bg_color"
        :link-text-color="parts.detail.text_color"
        :block-bg-color="parts.detail.modal_bg_color"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsBlockToDetail
        v-else-if="parts.layout_type === 'block_to_detail'"
        :id="`page_parts_${parts.document_id}`"
        :title="parts.detail.title"
        :sp-title="parts.detail.sp_title"
        :description="parts.detail.description"
        :sp-description="parts.detail.sp_description"
        :font-size="fontSizeClasses(parts.detail.font_size ?? '', parts.detail.sp_font_size ?? '')"
        :desc-font-size="fontSizeClasses(parts.detail.desc_font_size ?? '', parts.detail.sp_desc_font_size ?? '')"
        :font-weight="fontWeightClasses(parts.detail.font_weight)"
        :img-url="parts.detail.img_url"
        :sp-img-url="parts.detail.sp_img_url"
        :img-url-caption="parts.detail.img_url_caption"
        :sp-img-url-caption="parts.detail.sp_img_url_caption"
        :text-orientation="parts.detail.orientation"
        :link-url="parts.detail.url"
        :link-text="parts.detail.subtitle"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsPoint
        v-else-if="parts.layout_type === 'point'"
        :id="`page_parts_${parts.document_id}`"
        :title="parts.detail.title"
        :sp-title="parts.detail.sp_title"
        :img-url="parts.detail.img_url"
        :sp-img-url="parts.detail.sp_img_url"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <div
        v-else-if="parts.layout_type === 'blank'"
        :id="`page_parts_${parts.document_id}`"
        class="page-parts-blank"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      ></div>
      <PagePartsVideoAndText
        v-else-if="parts.layout_type === 'video_and_text'"
        :id="`page_parts_${parts.document_id}`"
        :title="parts.detail.title"
        :sp-title="parts.detail.sp_title"
        :description="parts.detail.description"
        :sp-description="parts.detail.sp_description"
        :text-orientation="parts.detail.orientation"
        :font-weight="fontWeightClasses(parts.detail.font_weight)"
        :font-size="fontSizeClasses(parts.detail.font_size ?? '', parts.detail.sp_font_size ?? '')"
        :desc-font-size="fontSizeClasses(parts.detail.desc_font_size ?? '', parts.detail.sp_desc_font_size ?? '')"
        :pc-url="parts.detail.video_url"
        :sp-url="parts.detail.sp_video_url"
        :pc-auto-play="parts.detail.video_auto_play"
        :sp-auto-play="parts.detail.sp_video_auto_play"
        :pc-loop-playback="parts.detail.video_loop_playback"
        :sp-loop-playback="parts.detail.sp_video_loop_playback"
        :pc-controls="parts.detail.video_controls"
        :sp-controls="parts.detail.sp_video_controls"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsEvaluation
        v-else-if="parts.layout_type === 'evaluation'"
        :id="`page_parts_${parts.document_id}`"
        :list="parts.list"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsImageSlideBig
        v-else-if="parts.layout_type === 'image_slide_big'"
        :id="`page_parts_${parts.document_id}`"
        :img-list="parts.list"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsBanner
        v-else-if="parts.layout_type === 'banner'"
        :id="`page_parts_${parts.document_id}`"
        :img-list="parts.list"
        :pc-img-column="parts.detail.pc_img_column"
        :sp-img-column="parts.detail.sp_img_column"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsModalBannerImage
        v-else-if="parts.layout_type === 'modal_banner_image'"
        :id="`page_parts_${parts.document_id}`"
        :banner-image="parts.detail.common_img_url"
        :sp-banner-image="parts.detail.sp_common_img_url"
        :modal-image="parts.detail.img_url"
        :sp-modal-image="parts.detail.sp_img_url"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
        :along-width-editor-text="meta?.isInterview"
      />
      <PagePartsAnchorLink
        v-else-if="parts.layout_type === 'anchor_link'"
        :id="`page_parts_${parts.document_id}`"
        :parts-list="parts.list"
        :display-type="parts.display_type"
        :page-type="pageType"
        :is-fixed-bottom="parts.detail.is_fixed_bottom"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsMessageCard
        v-else-if="parts.layout_type === 'message_card'"
        :id="`page_parts_${parts.document_id}`"
        :title="parts.detail.title"
        :sp-title="parts.detail.sp_title"
        :description="parts.detail.description"
        :sp-description="parts.detail.sp_description"
        :link-text="parts.detail.subtitle"
        :link-url="parts.detail.url"
        :background-img="parts.detail.img_url"
        :sp-background-img="parts.detail.sp_img_url"
        :font-size="fontSizeClasses(parts.detail.font_size ?? '', parts.detail.sp_font_size ?? '')"
        :desc-font-size="fontSizeClasses(parts.detail.desc_font_size ?? '', parts.detail.sp_desc_font_size ?? '')"
        :font-weight="fontWeightClasses(parts.detail.font_weight)"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsNewsList
        v-else-if="parts.layout_type === 'news_list'"
        :id="`page_parts_${parts.document_id}`"
        :news-group-id="parts.news_group_id"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsBreadcrumbs
        v-else-if="parts.layout_type === 'breadcrumbs'"
        :id="`page_parts_${parts.document_id}`"
        :breadcrumbs="parts.list"
        :font-size="fontSizeClasses(parts.detail.font_size ?? '', parts.detail.sp_font_size ?? '')"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsSimpleAccordion
        v-else-if="parts.layout_type === 'accordion'"
        :id="`page_parts_${parts.document_id}`"
        :accordions="parts.list"
        :page-type="pageType"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsExpertsList
        v-else-if="parts.layout_type === 'expert_list'"
        :id="`page_parts_${parts.document_id}`"
        :experts="parts.list"
        :link-url="parts.detail.url"
        :title="parts.detail.title"
      />
      <PagePartsPanelText
        v-else-if="parts.layout_type === 'panel_text'"
        :id="`page_parts_${parts.document_id}`"
        :title="parts.detail.title"
        :sp-title="parts.detail.sp_title"
        :description="parts.detail.description"
        :sp-description="parts.detail.sp_description"
        :font-size="fontSizeClasses(parts.detail.font_size ?? '', parts.detail.sp_font_size ?? '')"
        :title-font-weight="fontWeightClasses(parts.detail.font_weight)"
        :desc-font-size="fontSizeClasses(parts.detail.desc_font_size ?? '', parts.detail.sp_desc_font_size ?? '')"
        :panel1-text-color="parts.detail.text_color"
        :panel1-bg-color="parts.detail.bg_color"
        :panel2-text1="parts.detail.sub_text1"
        :sp-panel2-text1="parts.detail.sp_sub_text1"
        :panel2-emphasis="parts.detail.sub_text2"
        :sp-panel2-emphasis="parts.detail.sp_sub_text2"
        :panel2-text2="parts.detail.sub_text3"
        :sp-panel2-text2="parts.detail.sp_sub_text3"
        :emphasis-font-size="
          fontSizeClasses(parts.detail.sub_text_font_size ?? '', parts.detail.sp_sub_text_font_size ?? '')
        "
        :emphasis-font-weight="fontWeightClasses(parts.detail.sub_text_font_weight)"
        :panel2-bg-color="parts.detail.sub_bg_color"
        :panel2-text-color="parts.detail.sub_text_color"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsBannerWithText
        v-else-if="parts.layout_type === 'banner_with_text'"
        :id="`page_parts_${parts.document_id}`"
        :title="parts.detail.title"
        :sp-title="parts.detail.sp_title"
        :description="parts.detail.description"
        :sp-description="parts.detail.sp_description"
        :font-size="fontSizeClasses(parts.detail.font_size ?? '', parts.detail.sp_font_size ?? '')"
        :desc-font-size="fontSizeClasses(parts.detail.desc_font_size ?? '', parts.detail.sp_desc_font_size ?? '')"
        :color="parts.detail.text_color"
        :img-url="parts.detail.img_url"
        :sp-img-url="parts.detail.sp_img_url"
        :img-url-caption="parts.detail.img_url_caption"
        :sp-img-url-caption="parts.detail.sp_img_url_caption"
        :link-url="parts.detail.url"
        :link-text="parts.detail.subtitle"
        :link-text-color="parts.detail.sub_text_color"
        :link-bg-color="parts.detail.bg_color"
        :text-align="parts.detail.text_align"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <!-- 刺繍限定カートを使用する際に復活させる -->
      <!-- <template v-else-if="parts.layout_type === 'embroidery_limited_cart' && parts.product && parts.product.skus">
        <ClientOnly>
          <EmbroideryCart
            :id="`page_parts_${parts.document_id}`"
            :product="parts.product"
            :description="parts.detail.description ?? ''"
            :index="index"
            :cart-index="getCartIndex(parts.document_id)"
            class="page-parts-section"
            :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
          />
        </ClientOnly>
      </template> -->
      <template v-else-if="parts.layout_type === 'cart' && parts.product && parts.product.skus">
        <ClientOnly>
          <PagePartsCart
            :id="`page_parts_${parts.document_id}`"
            ref="cartRefs"
            :product="parts.product"
            :list-parts="parts.list"
            :subtitle="parts.detail.subtitle ?? ''"
            :description="parts.detail.description ?? ''"
            :index="index"
            :cart-index="getCartIndex(parts.document_id)"
            :page-type="pageType"
            class="page-parts-section"
            :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
            :delivery-lt-prefectures="deliveryLtPrefectures"
          />
        </ClientOnly>
        <div v-if="pageType !== 'lpnh'" id="page_parts_bottom_cart_button" :class="$style.bottom_cart_button">
          <AtomsConsumerButton
            :id="`page_parts_bottom_cart_${parts.document_id}`"
            variant="primary"
            block
            @click.prevent="addCartBottomNavigation()"
            >カートに追加</AtomsConsumerButton
          >
        </div>
      </template>
      <template v-else-if="parts.layout_type === 'multi_cart' && parts.products">
        <ClientOnly>
          <PagePartsMultiCart
            :id="`page_parts_${parts.document_id}`"
            :products="parts.products"
            class="page-parts-section"
            :is-wrapping-recommend="parts.detail.is_wrapping_recommend"
            :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
          />
        </ClientOnly>
      </template>
      <template v-else-if="parts.layout_type === 'group_wrapping_multi_cart' && parts.products">
        <ClientOnly>
          <PagePartsGroupWrappingMultiCart
            :id="`page_parts_${parts.document_id}`"
            :products="parts.products"
            :gift-pattern-ids="parts.gift_pattern_ids"
            :package-size="parts.detail.package_size_for_wrapping ?? 0"
            class="page-parts-section"
            :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
          />
        </ClientOnly>
      </template>
      <template v-else-if="parts.layout_type === 'custom_image_multi_cart' && parts.products">
        <ClientOnly>
          <PagePartsCustomImageMultiCart
            :id="`page_parts_${parts.document_id}`"
            :products="parts.products as PagePartsProductType[]"
            :is-wrapping-recommend="parts.detail.is_wrapping_recommend"
            :img-list="parts.list"
            class="page-parts-section"
            :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
          />
        </ClientOnly>
      </template>
      <PagePartsNoValidationProductList
        v-else-if="parts.layout_type === 'no_validation_product_list' && parts.products"
        :id="`page_parts_${parts.document_id}`"
        :products="parts.products as PagePartsProductType[]"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsRankingView
        v-else-if="parts.layout_type === 'ranking_view' && parts.products"
        :id="`page_parts_${parts.document_id}`"
        :product-ids="parts.product_ids"
        :products="parts.products as PagePartsProductType[]"
        :link-url="parts.detail.url"
        :is-hide-ranking-num="parts.detail.is_hide_ranking_num"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsReviewList
        v-else-if="parts.layout_type === 'review_list'"
        :id="`page_parts_${parts.document_id}`"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
        :review-group-id="parts.review_group_id ?? ''"
      />
      <PagePartsProductList
        v-else-if="parts.layout_type === 'product_list' && productListParts"
        :id="`page_parts_${parts.document_id}`"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
        :page-parts-category="parts.category"
        :product-categories="productListCategories"
        :product-list-data="productListData"
        :product-search-query-vars="apiProductSearchQueryVars"
        @fetched-product-search-query="fetchProductSearchQuery"
        @set-product-search-query-vars="setProductSearchQueryVars"
      />
      <PagePartsCategories
        v-else-if="parts.layout_type === 'categories'"
        :id="parts.document_id"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <PagePartsTopSection
        v-else-if="parts.layout_type === 'top_section'"
        :id="`page_parts_${parts.document_id}`"
        :pc-title="parts.detail.title"
        :sp-title="parts.detail.sp_title"
        :text="parts.detail.description"
        :text-align="parts.detail.text_align"
        :image-pc="parts.detail.img_url"
        :image-sp="parts.detail.sp_img_url"
        :source-type="detectSourceType(parts.detail.is_movie)"
        :movie-id="parts.detail.youtube_id"
        class="page-parts-section"
        :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
      />
      <template v-else-if="!isChild && parts.layout_type === 'tab_group' && firstTabGroupIndex === index">
        <div id="navigation" />
        <div :class="$style.tab_group_accordion">
          <div
            v-for="(navigation, i) in navigationList"
            :key="`accordion_${navigation.document_id}`"
            :class="$style.accordion_group"
          >
            <MoleculesConsumerAccordion v-if="i > 0" :class="$style.accordion">
              <template #title>
                <h4 :class="$style.title">{{ navigation.detail.title }}</h4>
              </template>
              <template #content>
                <PagePartsAccordionGroup
                  :tab-title="navigation.detail.title"
                  :product="cartParts[0]?.product"
                  :child-parts-list="navigation.childPartsList"
                  :page-type="pageType"
                  class="page-parts-section"
                  :class="blankClasses(navigation.detail.pc_margin, navigation.detail.sp_margin)"
                />
              </template>
            </MoleculesConsumerAccordion>
          </div>
        </div>
        <PagePartsTabGroup
          :tab-title="parts.detail.title"
          :product="cartParts[0]?.product"
          :child-parts-list="parts.childPartsList"
          :page-type="pageType"
          class="page-parts-section"
          :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
        />
      </template>
      <div
        v-else-if="!isChild && parts.layout_type === 'accordion_group'"
        :class="[
          $style.accordion_group,
          'page-parts-section',
          blankClasses(parts.detail.pc_margin, parts.detail.sp_margin),
        ]"
      >
        <MoleculesConsumerAccordion :class="$style.accordion">
          <template #title>
            <h4 :class="$style.title">{{ parts.detail.title }}</h4>
          </template>
          <template #content>
            <PagePartsAccordionGroup
              :tab-title="parts.detail.title"
              :product="cartParts[0]?.product"
              :child-parts-list="parts.childPartsList"
              :page-type="pageType"
              class="page-parts-section"
              :class="blankClasses(parts.detail.pc_margin, parts.detail.sp_margin)"
            />
          </template>
        </MoleculesConsumerAccordion>
      </div>
    </template>
  </template>
</template>

<script setup lang="ts">
import type { PagePartsProductType, ProductFilterV2Props } from "~/types/product"
import {
  usePublicLogilessOutboundDeliveryCalcDeliveryLtPrefecturesQuery,
  usePublicProductCategoryAllForSiteQuery,
} from "~/gql/urql.generated"
import type { PublicLogilessOutboundDeliveryCalcDeliveryLtPrefecturesQuery } from "~/types/type.generated"
import {
  blankClasses,
  fontWeightClasses,
  fontSizeClasses,
  alignClasses,
  detectSourceType,
} from "~/utils/functions/page_parts"
import { PagePartsSetType } from "~/types/page"
import PagePartsCart from "~/components/pageParts/Cart.vue"
import type {
  ApiProductSearchQuery,
  ApiProductSearchResponse,
  ApiProductSearchResponse_Product,
} from "~/types/server/api/product"
import { ProductAllSearchInput_Sort } from "~/const/product"

const cartRefs = ref<InstanceType<typeof PagePartsCart>[]>([])

type SetMeta = {
  isInterview: boolean
}

type Props = {
  pageParts: PagePartsSetType[]
  meta?: SetMeta
  isChild?: boolean
  childPartsIds: (string | undefined)[]
  deliveryLtPrefectures?: PublicLogilessOutboundDeliveryCalcDeliveryLtPrefecturesQuery["publicLogilessOutboundDeliveryCalcDeliveryLtPrefectures"]
}

const props = withDefaults(defineProps<Props>(), {
  meta: undefined,
  isChild: false,
  childPartsIds: () => [],
  deliveryLtPrefectures: () =>
    ({
      default_prefecture: "",
      prefectures: [],
    } as PublicLogilessOutboundDeliveryCalcDeliveryLtPrefecturesQuery["publicLogilessOutboundDeliveryCalcDeliveryLtPrefectures"]),
})

const emit = defineEmits<{
  (e: "fetchedProductList", products: ApiProductSearchResponse_Product[]): void
}>()

const route = useRoute()
const pageType = route.path.split("/")[1]

const navigationList = computed(() => {
  return props.pageParts?.filter((pagePart) => pagePart.layout_type === "tab_group" && pagePart.is_status)
})

const addCartBottomNavigation = async (): Promise<void> => {
  if (cartRefs.value[0]) await cartRefs.value[0].callAddCart()
}

const firstTabGroupIndex = computed(() => props.pageParts.findIndex((item) => item.layout_type === "tab_group"))

const cartParts = computed(() => {
  return props.pageParts?.filter((parts) => parts.layout_type === "cart")
})

const productListParts = computed(() => {
  return props.pageParts.find((parts) => parts.layout_type === "product_list")
})
const productListCategories = ref<ProductFilterV2Props["categories"]>()
const productListData = ref<ApiProductSearchResponse>()

const publicProductCategoryAllForSiteQueryVariables = ref({
  page: 1,
  limit: 100,
  filter: {
    parent_category_id: productListParts?.value?.category?.parent_category_id,
  },
})
const { executeQuery: _publicProductCategoryAllForSiteQueryExec } = usePublicProductCategoryAllForSiteQuery({
  requestPolicy: "cache-first",
  pause: true,
  variables: publicProductCategoryAllForSiteQueryVariables.value,
})

const { PRODUCT_LIST_LIMIT } = useProductsPage()
const queryCsv2ArrayFilter = (queryValue: string | undefined) => queryValue?.split(",").filter((v) => v !== "") ?? []
const buildApiProductSearchQueryVars = (): ApiProductSearchQuery => {
  return {
    keywords: queryCsv2ArrayFilter(route.query.keywords?.toString()).filter((v) => !!v.trim()),
    category: productListParts?.value?.category?.document_id,
    sex: queryCsv2ArrayFilter(route.query.sex?.toString()) as ApiProductSearchQuery["sex"],
    fabric: queryCsv2ArrayFilter(route.query.fabric?.toString()) as ApiProductSearchQuery["fabric"],
    season: queryCsv2ArrayFilter(route.query.season?.toString()) as ApiProductSearchQuery["season"],
    silhouette: queryCsv2ArrayFilter(route.query.silhouette?.toString()) as ApiProductSearchQuery["silhouette"],
    wear_category: queryCsv2ArrayFilter(
      route.query.wear_category?.toString(),
    ) as ApiProductSearchQuery["wear_category"],
    size: queryCsv2ArrayFilter(route.query.size?.toString()) as ApiProductSearchQuery["size"],
    ...(route.query.is_in_stock === "1" ? { is_in_stock: "1" } : {}),
    ...(route.query.is_outlet === "1" ? { is_outlet: "1" } : {}),
    ...(route.query.campaign ? { campaign: route.query.campaign as ApiProductSearchQuery["campaign"] } : {}),
    page: route.query.page?.toString() ?? "1",
    limit: PRODUCT_LIST_LIMIT,
    sort: (route.query.sort as ProductAllSearchInput_Sort) ?? ProductAllSearchInput_Sort.Recommendation,
  }
}

const apiProductSearchQueryVars = ref<ApiProductSearchQuery>(buildApiProductSearchQueryVars())
const fetchProductListData = async () => {
  const productSearchResponse = await $fetch<ApiProductSearchResponse>("/api/product/search", {
    query: apiProductSearchQueryVars.value,
  })
  if (productSearchResponse) productListData.value = productSearchResponse
}

const setProductSearchQueryVars = (value: ApiProductSearchQuery) => {
  apiProductSearchQueryVars.value = value
}

const fetchProductListAndCategories = async () => {
  if (productListParts?.value?.category?.child_categories?.length) {
    productListCategories.value = productListParts?.value?.category?.child_categories?.map((category) => ({
      document_id: category.document_id,
      name: category.name ?? "",
      slug: category.slug,
    }))
  } else {
    publicProductCategoryAllForSiteQueryVariables.value.filter.parent_category_id =
      productListParts?.value?.category?.parent_category_id
    if (_publicProductCategoryAllForSiteQueryExec().isPaused) _publicProductCategoryAllForSiteQueryExec().resume()
    const { data: categoryData } = await _publicProductCategoryAllForSiteQueryExec()
    const categories = categoryData.value?.publicProductCategoryAllForSite?.items ?? []
    productListCategories.value = categories.map((category) => ({
      document_id: category.document_id,
      name: category.name ?? "",
      slug: category.slug,
    }))
  }
  await fetchProductSearchQuery()
}

const fetchProductSearchQuery = async () => {
  apiProductSearchQueryVars.value = buildApiProductSearchQueryVars()
  await fetchProductListData()
}

defineExpose({
  fetchProductSearchQuery,
})

const deliveryLtPrefectures = ref<
  PublicLogilessOutboundDeliveryCalcDeliveryLtPrefecturesQuery["publicLogilessOutboundDeliveryCalcDeliveryLtPrefectures"]
>(props.deliveryLtPrefectures)

const { data: _calcDeliveryLtPrefecturesData, executeQuery: _calcDeliveryLtPrefecturesExecuteQuery } =
  usePublicLogilessOutboundDeliveryCalcDeliveryLtPrefecturesQuery({
    requestPolicy: "cache-first",
    pause: true,
  })

if (props.pageParts) {
  // propsで渡されなかった場合はAPIから取得
  if (!deliveryLtPrefectures.value && props.pageParts.some((parts) => parts.layout_type === "cart")) {
    if (_calcDeliveryLtPrefecturesExecuteQuery().isPaused) _calcDeliveryLtPrefecturesExecuteQuery().resume()
    await _calcDeliveryLtPrefecturesExecuteQuery()

    deliveryLtPrefectures.value = _calcDeliveryLtPrefecturesData.value
      ?.publicLogilessOutboundDeliveryCalcDeliveryLtPrefectures ?? {
      default_prefecture: "",
      prefectures: [],
    }
  }
}

if (productListParts?.value) await fetchProductListAndCategories()

const getCartIndex = (partsId: string) => {
  if (!cartParts.value || cartParts.value.length === 0) return 0
  const index = cartParts.value.findIndex((parts) => parts.document_id === partsId)
  return index + 1
}

watch(
  () => productListData.value?.products,
  (newProducts) => {
    if (newProducts) {
      emit("fetchedProductList", newProducts)
    }
  },
  { immediate: true },
)

watch(
  () => productListParts?.value?.category?.parent_category_id,
  async () => {
    await fetchProductListAndCategories()
  },
)
</script>

<style scoped module lang="scss">
.bottom_cart_button {
  display: flex;
  flex-direction: column;
  justify-content: center;
  z-index: 10;
  position: fixed;
  bottom: 1rem;
  width: 100%;
  padding: 0 1rem;
  @include md {
    padding: 0 2rem;
    bottom: 2rem;
  }
  button {
    border-radius: 2rem !important;
    width: calc(100% - 2rem);
    max-width: 580px;
    height: 3rem;
    margin: 0 auto;
    @include md {
      height: 4rem;
      width: calc(100% - 4rem);
    }
  }
}

.accordion_group {
  @include container-lg;
  .accordion {
    box-sizing: border-box;
    border-bottom: 1px solid $secondary-40;
    .title {
      font-size: $font-size-16;
      text-align: left;
      height: 2.5rem;
      display: flex;
      align-items: center;
    }
  }
}

.tab_group_accordion {
  @include container-lg;
  margin-bottom: 3rem;
  @include lg {
    margin-bottom: 4rem;
  }
  .accordion_group {
    padding: 0;
    .accordion {
      // Cartパーツの右カラムに合わせる
      @include lg {
        margin-left: auto;
        margin-right: 0;
        width: 50%;
        max-width: 600px;
      }
      :global {
        .page-parts-section {
          padding: 0;
        }
      }
    }
  }
}
</style>
